import axios from "axios";
import { problemMessage as scholarshipsProblemMessage } from "./scholarshipProblems";
import { loansProblemMessage } from "./loansProblems";

// A Loans service code reads first as the Loans service means it: some mean something else in scholarships
const problemMessage = (code, api = "scholarships") =>
  (api === "scholarships" ? null : loansProblemMessage(code)) ?? scholarshipsProblemMessage(code);

/**
 * Clients for the HEC APIs under the main API (the gateway): scholarships here, the Loans service's
 * in hliLists.js and abroadLoans.js. The token comes from the axios interceptor (src/auth/http.js).
 *
 * Every call resolves to `{ data, etag }`. Pass the last `etag` of a resource as `ifMatch` on any
 * change to it; the contract refuses changes without it (428) or with a stale one (412).
 */
export const SCHOLARSHIPS_API = `${process.env.REACT_APP_BASE_URL}hec/scholarships/`;

const DEFAULT_PAGE_SIZE = 20;

// One key per user action; reuse it only when retrying that same action
export const newIdempotencyKey = () =>
  window.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;

const request = async (base, method, path, { params, body, ifMatch, idempotencyKey, headers, ...config } = {}) => {
  const response = await axios.request({
    method,
    url: `${base}${path}`,
    params,
    data: body,
    headers: {
      ...(ifMatch && { "If-Match": ifMatch }),
      ...(idempotencyKey && { "Idempotency-Key": idempotencyKey }),
      ...headers,
    },
    ...config,
  });
  return { data: response.data, etag: response.headers?.etag ?? null };
};

// A client for one API, `base` ending with a slash
export const apiClient = (base) => ({
  get: (path, options) => request(base, "get", path, options),
  post: (path, body, options) => request(base, "post", path, { ...options, body }),
  put: (path, body, options) => request(base, "put", path, { ...options, body }),
  patch: (path, body, options) => request(base, "patch", path, { ...options, body }),
  delete: (path, options) => request(base, "delete", path, options),
});

export const scholarshipsApi = apiClient(SCHOLARSHIPS_API);

// Paged lists take a 0-based `page` and a `size` of 1-100
export const pageParams = ({ page = 0, size = DEFAULT_PAGE_SIZE, ...filters } = {}) => {
  // Empty filters are left out, so "all states" sends no `state`
  const set = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v !== null && v !== undefined));
  return { ...set, page, size };
};

/**
 * The RFC 9457 problem behind a failed call, or null for a network failure. `code` is the last
 * part of `type` (`urn:hec:problem:scholarships:not-draft` → `not-draft`, the same for
 * `hli-lists` and `abroad-loans`); branch on it, never on `detail`. `errors` lists every violated
 * rule as `{ field, code }` (the Loans APIs call them `violations`).
 */
export const problemOf = (error) => {
  const response = error?.response;
  if (!response) return null;
  const body = response.data && typeof response.data === "object" ? response.data : {};
  const type = typeof body.type === "string" ? body.type : "";
  const api = type.match(/^urn:hec:problem:(scholarships|hli-lists|abroad-loans):/)?.[1] ?? null;
  const code = api ? type.split(":").pop() : null;
  const errors = Array.isArray(body.errors) ? body.errors : Array.isArray(body.violations) ? body.violations : [];
  return { status: response.status, api, code, errors, body };
};

// Field errors from a problem, keyed by field name, ready for form inputs
export const fieldErrorsOf = (problem) =>
  Object.fromEntries((problem?.errors ?? []).filter((e) => e.field).map((e) => [e.field, problemMessage(e.code, problem.api ?? undefined) ?? "This value is not valid."]));

// One user-facing sentence for a failed call
export const errorMessageOf = (error, fallback = "Something went wrong. Please try again.") => {
  const problem = problemOf(error);
  if (!problem) return "Could not reach the server. Check your connection and try again.";
  if (problem.status === 401) return "Your session has ended. Please sign in again.";
  // A refusal the API explains (e.g. an account not linked to an institution) says why; any other 403 does not
  const known = problemMessage(problem.code, problem.api ?? undefined);
  if (known) return known;
  if (problem.status === 403) return "You do not have access to this.";
  return fallback;
};

export { problemMessage };
